import React from 'react';
import { Helmet } from 'react-helmet-async';

const DEFAULT_IMAGE = 'https://www.taalsetu.com/og-image.jpg';

/**
 * SEO component for page-level meta tags, Open Graph, Twitter Cards,
 * JSON-LD structured data, and BreadcrumbList schema.
 *
 * @param {string}   title        - Page title
 * @param {string}   description  - Meta description
 * @param {string}   keywords     - Comma-separated keywords
 * @param {string}   type         - OG type (default: 'website')
 * @param {string}   url          - Canonical URL
 * @param {string}   image        - OG image URL (absolute or relative)
 * @param {object|object[]} schemaMarkup - JSON-LD schema (single object or array)
 * @param {Array<{name:string, url:string}>} breadcrumbs - Breadcrumb items for BreadcrumbList schema
 * @param {boolean}  noindex      - Keep the page out of search results (still follows links)
 */
const SEO = ({ title, description, keywords, type, url, image, schemaMarkup, breadcrumbs, noindex }) => {
  const absoluteImage = image
    ? (image.startsWith('http') ? image : `https://www.taalsetu.com${image}`)
    : DEFAULT_IMAGE;

  // Support single schema object or array of schemas
  const schemas = schemaMarkup
    ? (Array.isArray(schemaMarkup) ? schemaMarkup : [schemaMarkup])
    : [];

  // Build BreadcrumbList schema if breadcrumbs are provided
  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0
    ? {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((crumb, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: crumb.name,
          item: crumb.url,
        })),
      }
    : null;

  const allSchemas = breadcrumbSchema ? [...schemas, breadcrumbSchema] : schemas;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="title" content={title} />
      {keywords && <meta name="keywords" content={keywords} />}
      {noindex && <meta name="robots" content="noindex,follow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type || 'website'} />
      <meta property="og:url" content={url || 'https://www.taalsetu.com'} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="Taalsetu" />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:locale:alternate" content="hi_IN" />
      <meta property="og:locale:alternate" content="mr_IN" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url || 'https://www.taalsetu.com'} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={absoluteImage} />

      {/* Canonical Link */}
      <link rel="canonical" href={url || 'https://www.taalsetu.com'} />

      {/* JSON-LD Structured Data (supports multiple schemas) */}
      {allSchemas.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(allSchemas.length === 1 ? allSchemas[0] : allSchemas)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
